import request from '@/utils/request'
import { buildApiV1Path } from '@/utils/apiPath'

// ==================== 类型定义 ====================

/**
 * 研究状态类型
 */
export type StudyStatus = 'ONGOING' | 'PAUSED' | 'COMPLETED'

/**
 * SUPER 用户统计数据
 */
export interface SuperStatsResponse {
  studyCount: number
  organizationCount: number
  userCount: number
  dictionaryCount: number
}

/**
 * 最近研究项
 */
export interface RecentStudy {
  id: string
  code: string
  title: string
  status: string
  phase?: string
  createdAt: string
}

/**
 * ADMIN 用户统计数据
 */
export interface AdminStatsResponse {
  myStudyCount: number
  ongoingCount: number
  pausedCount: number
  completedCount: number
  recentStudies: RecentStudy[]
}

/**
 * 研究信息
 */
export interface StudyInfo {
  id: string
  code: string
  title: string
  description?: string
  indications?: string
  status: string
  phase?: string
  progress?: number
}

/**
 * 角色级别
 */
export type RoleLevel = 'STUDY' | 'SITE'

/**
 * 我的研究列表项
 */
export interface MyStudyItem {
  id: string // 成员ID
  studyId: string
  study: StudyInfo
  roleCode: string
  roleDisplay: string
  roleLevel: RoleLevel // 角色级别：STUDY=研究级, SITE=中心级
  isFavorite: boolean
  lastVisitedAt?: string
  memberCreatedAt: string // 成员记录创建时间（授权时间）
  authorizedCenters: string[] // 授权中心 ID 列表（UUID 字符串）
}

/**
 * 我的研究列表查询参数
 */
export interface MyStudiesQueryParams {
  userId?: number | string
  status?: StudyStatus
  keyword?: string
  page?: number
  pageSize?: number
  sortBy?: 'createdAt' | 'lastVisitedAt'
  sortOrder?: 'asc' | 'desc'
}

/**
 * 我的研究列表响应
 */
export interface MyStudiesResponse {
  list: MyStudyItem[]
  total: number
  page: number
  pageSize: number
}

/**
 * 验证权限响应
 */
export interface VerifyPermissionResponse {
  hasPermission: boolean
  roleCode?: string
  authorizedCenters?: string[] // 授权中心 ID 列表（UUID 字符串）
}

// ==================== API 函数 ====================

/**
 * 获取 SUPER 用户统计数据
 */
export function getSuperStats(): Promise<{ success: boolean; data: SuperStatsResponse }> {
  return request.get(buildApiV1Path('dashboard', 'stats', 'super'))
}

/**
 * 获取 ADMIN 用户统计数据
 */
export function getAdminStats(userId: number | string): Promise<{ success: boolean; data: AdminStatsResponse }> {
  const useBff = ((import.meta as any).env?.VITE_DEV_USE_BFF === 'true')
  const params = useBff ? {} : { userId }
  return request.get(buildApiV1Path('dashboard', 'stats', 'admin'), { params })
}

/**
 * 获取我的研究列表（COMMON 用户）
 */
export function getMyStudies(params: MyStudiesQueryParams): Promise<{ success: boolean; data: MyStudiesResponse }> {
  const useBff = ((import.meta as any).env?.VITE_DEV_USE_BFF === 'true')
  const { userId, ...rest } = params || {}
  const finalParams = useBff ? rest : params
  return request.get(buildApiV1Path('dashboard', 'my-studies'), { params: finalParams })
}

/**
 * 添加研究到收藏
 */
export function addFavoriteStudy(memberId: string): Promise<{
  success: boolean
  data: { isFavorite: boolean }
  message: string
}> {
  return request.post(buildApiV1Path('dashboard', 'study-members', memberId, 'favorite'))
}

/**
 * 从收藏中移除研究
 */
export function removeFavoriteStudy(memberId: string): Promise<{
  success: boolean
  data: { isFavorite: boolean }
  message: string
}> {
  return request.delete(buildApiV1Path('dashboard', 'study-members', memberId, 'favorite'))
}

/**
 * 更新最后访问时间
 */
export function updateLastVisited(memberId: string): Promise<{
  success: boolean
  data: { lastVisitedAt: string }
  message: string
}> {
  return request.put(buildApiV1Path('dashboard', 'study-members', memberId, 'last-visited'))
}

/**
 * 验证研究权限
 */
export function verifyStudyPermission(
  studyId: string,
  userId?: number | string
): Promise<{ success: boolean; data: VerifyPermissionResponse }> {
  const useBff = ((import.meta as any).env?.VITE_DEV_USE_BFF === 'true')
  const params = useBff ? { studyId } : { studyId, userId }
  return request.get(buildApiV1Path('dashboard', 'verify-permission'), { params })
}
